import {
  AfterViewInit,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {AbstractControl, ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, NgControl} from '@angular/forms';
import * as _ from 'lodash';
import { inputValidationMessage } from 'src/shared/validator/helper';

@Component({
  selector: 'dcu-input-text',
  templateUrl: './input-text.component.html',
  styleUrls: ['./input-text.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: InputTextComponent,
    multi: true,
  }],
})
export class InputTextComponent implements OnInit, DoCheck, AfterViewInit, ControlValueAccessor {

  @Output()
  eventEmitter = new EventEmitter();

  @Output()
  onBlurValue: EventEmitter<string> = new EventEmitter();

  @Output()
  inputChange: EventEmitter<string> = new EventEmitter();

  @Input()
  placeholder = '';

  @Input()
  required = false;

  @Input()
  readonly = false;

  @Input()
  disabled = false;

  @Input()
  type = 'text';

  @Input()
  min = -9999;

  @Input()
  max = 9999;

  @Input()
  maxLength = 9999999;

  @Input()
  showLabel = false;

  @Input()
  label = '';

  @Input()
  autofocus = false;

  @ViewChild('input', {static: false})
  element: ElementRef;

  @Input()
  formattedValue = false;

  @Input()
  isDecimal = false;

  @Input()
  allowNegative: boolean;

  @Input()
  maxIntegerNumber: number; // Specify max integer number if this input is decimal number
  @Input()
  maxFractionNumber: number; // Specify max fraction number if this input is decimal number
  @Input()
  minIntegerNumber: number = -9999;

  @Input()
  fieldId: string;

  @Input() tooltipLabel = '';
  @Input()
  append: string;
  @Input()
  additionalInfo: string;

  @Input() iconUrl: string;
  @Input() isNoOfDays = false;

  onChange;
  onTouch;

  inputControl: any = new FormControl(null);
  parentControl: AbstractControl;
  valueChange = false;
  validatorsBackup: any = null;
  tempMaxLength: number;

  validationMessage = '';

  @Input() customClass: string;

  constructor(
    private injector: Injector
  ) {
    this.allowNegative = false;
  }

  ngOnInit() {
    this.placeholder = this.required ? `${this.placeholder}` : this.placeholder;
    if(!this.maxIntegerNumber) {
      this.maxIntegerNumber = 13;
    }
    if(!this.maxFractionNumber) {
      this.maxFractionNumber = 4;
    }
    if (this.type === 'number' || this.isDecimal) {
      this.tempMaxLength = this.maxLength;
    }
    if (this.isDecimal) {
      this.placeholder = `Max ${this.maxIntegerNumber + this.maxFractionNumber} integers with ${this.maxFractionNumber} decimals.`;
    }
    if(this.type === 'phone') {
      this.placeholder = 'eg: (+65) 1234-5678-9';
    }
    this.setDisabledState(this.disabled);
  }

  ngAfterViewInit() {
    this.parentControl = this.injector.get(NgControl).control;
  }

  writeValue(value) {
    // trim the value
    if (value && 'number' === this.type && this.formattedValue) {
      value = _.trim(value);
      value = _.toFinite(value).toLocaleString('en-GB', {
        maximumFractionDigits: this.maxFractionNumber
      });
    }

    this.inputControl.setValue(value);
  }

  reset() {
    this.inputControl.reset();
  }

  registerOnChange(fn) {
    this.onChange = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.inputControl.disable();
    } else {
      this.inputControl.enable();
    }
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  ngDoCheck() {
    if (this.parentControl) {
      this.validationMessage = inputValidationMessage(this.parentControl);
    }
  }

  onInputChange() {
    // backup validators
    if (_.isNull(this.validatorsBackup)) {
      this.validatorsBackup = this.parentControl.asyncValidator;
    }
    // pending validator
    this.parentControl.clearAsyncValidators();
    this.parentControl.updateValueAndValidity();
    this.parentControl.markAsTouched();
    this.valueChange = true;

    // pending status = true when validate async not done.
    this.parentControl.markAsPending();
    if (this.onChange) {
      let newValue = this.inputControl.value;

      if (_.isNull(newValue) || _.size(newValue) === 0) {
        this.onChange(null);
      } else {
        if (_.toNumber(newValue) < this.minIntegerNumber
        && this.type === 'number') {
          this.onChange(null);
          this.parentControl.reset();
          this.inputChange.emit(null);

          return;
        }

        if(this.type!=='phone') {
          newValue = _.trim(newValue);
        }
        newValue = this.correctValue(newValue);
        this.onChange(newValue);
        this.inputChange.emit(newValue);
        if(_.isEmpty(newValue)) {
          this.parentControl.reset();
        }
      }
    }
  }

  onFocusout() {
    // If data not change then stop
    if (!this.valueChange) { return; }

    this.valueChange = false;
    this.eventEmitter.emit({blur: true});
    // Update async validators
    this.parentControl.markAsTouched();
    this.parentControl.setAsyncValidators(this.validatorsBackup);
    this.parentControl.updateValueAndValidity();
  }

  isGoodValue(value) {
    if (value && _.size(value) > 0) {
      return true;
    }

    return false;
  }

  onBlur(event) {
    if (event && event.target) {
      this.onBlurValue.emit(event.target.value);
    }
  }

  // the purpose of this function is to format number only
  private correctValue(value: string) {
    if (this.type === 'number' && this.element) {
      const caretPos = this.element.nativeElement.selectionStart;
      const initialValueLength = _.size(value);
      if (this.formattedValue) {
        value = this.getFormatValue(value);
      } else {
        value = this.getNoFormatValue(value);
      }
      // count number of '.' in value
      const dotNumber = (value.match(/\./g) || []).length;
      // count number of ',' in value
      const commaNumber = (value.match(/,/g) || []).length;
      // count number of '-' in value
      const hyphenNumber = (value.match(/-/g) || []).length;
      this.maxLength = this.tempMaxLength + dotNumber + commaNumber  + hyphenNumber;
      this.element.nativeElement.value = value;
      this.updateCaretPos(caretPos, initialValueLength);
    } else if (this.type === 'phone' && this.element) {
      value =  value.replace(/[^0-9\s+()-]/g, '');
      this.element.nativeElement.value = value;
    }
    return value;
  }

  private getNoFormatValue(value): string {
    const caretPos = this.element.nativeElement.selectionStart;
    let isDelete = false;
    if (this.parentControl.value) {
      isDelete = this.parentControl.value.length > value.length;
    }
    if (this.isDecimal) {
      if (value.indexOf('.') >= 0) {
        const decimalPos = value.indexOf('.');
        let leftSide = value.substring(0, decimalPos);
        let rightSide = value.substring(decimalPos + 1);
        if (!isDelete) {
          if (this.getLengthOfNumberOnly(leftSide) > this.maxIntegerNumber || rightSide.length > this.maxFractionNumber) {
            value = this.removeCharacter(caretPos, value);
          } else {
            leftSide = this.formatNumberOnly(leftSide);
            rightSide = this.formatNumberOnly(rightSide);
            value = leftSide + "." + rightSide;
          }
        }
      } else {
        if (!isDelete && this.getLengthOfNumberOnly(value) > this.maxIntegerNumber) {
          value = this.removeCharacter(caretPos, value);
        } else {
          value = this.formatNumberOnly(value);
        }
      }
    } else if (this.isNoOfDays) {
      if (value.indexOf('.') >= 0) {
        const decimalPos = value.indexOf('.');
        let leftSide = value.substring(0, decimalPos);
        let rightSide = value.substring(decimalPos + 1);
        if (!isDelete) {
          if (this.getLengthOfNumberOnly(leftSide) > 3 || rightSide.length > 1) {
            value = this.removeCharacter(caretPos, value);
          } else {
            leftSide = this.formatNumberOnly(leftSide);
            if (!_.isEqual(rightSide, '0') && !_.isEqual(rightSide, '5')) {
              rightSide = '';
            }
            rightSide = this.formatNumberOnly(rightSide);
            value = leftSide + "." + rightSide;
          }
        }
      } else {
        if (!isDelete && this.getLengthOfNumberOnly(value) > 3) {
          value = this.removeCharacter(caretPos, value);
        } else {
          value = this.formatNumberOnly(value);
        }
      }
    } else {
      value = this.formatNumberOnly(value);
    }
    return value;
  }

  private getFormatValue(value: string) {
    const caretPos = this.element.nativeElement.selectionStart;
    let isDelete = false;
    if (this.parentControl.value) {
      isDelete = this.parentControl.value.length > value.length;
    }
    if (this.isDecimal) {
      if (value.indexOf('.') >= 0) {
        const decimalPos = value.indexOf('.');
        let leftSide = value.substring(0, decimalPos);
        let rightSide = value.substring(decimalPos + 1);
        if (!isDelete) {
          if (this.getLengthOfNumberOnly(leftSide) > this.maxIntegerNumber || rightSide.length > this.maxFractionNumber) {
            value = this.removeCharacter(caretPos, value);
          } else {
            leftSide = this.formatNumberWithSeparator(leftSide);
            rightSide = this.formatNumberOnly(rightSide);
            value = leftSide + "." + rightSide;
          }
        }
      } else {
        if (!isDelete && this.getLengthOfNumberOnly(value) > this.maxIntegerNumber) {
          value = this.removeCharacter(caretPos, value);
        } else {
          value = this.formatNumberWithSeparator(value);
        }
      }
    } else {
      value = this.formatNumberWithSeparator(value);
    }
    return value;
  }

  private removeCharacter(caretPos: number, value: string): string {
    const tempValue = value.slice(0, caretPos - 1) + value.slice(caretPos);
    return tempValue;
  }

  private getLengthOfNumberOnly(value: string): number {
    const commaNumber = (value.match(/,/g) || []).length;
    const hyphenNumber = (value.match(/-/g) || []).length;
    return value.length - commaNumber - hyphenNumber;
  }

  private updateCaretPos(caretPos, initialLength) {
    const updatedLen = _.size(this.element.nativeElement.value);
    // re-update new pointer position
    caretPos = updatedLen - initialLength + caretPos;
    this.element.nativeElement.setSelectionRange(caretPos, caretPos);
  }

  private formatNumberWithSeparator(value: string) {
    let firstChar = value.substring(0,1);
    firstChar  = firstChar.replace(/[^-\d]/g, '');
    if(this.allowNegative && firstChar === '-') {
      let remainValue = value.substring(1);
      remainValue = remainValue.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return firstChar.concat(remainValue);
    }
    return value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  private formatNumberOnly(value: string): string {
    let firstChar = value.substring(0,1);
    firstChar  = firstChar.replace(/[^-\d]/g, '');
    if(this.allowNegative && firstChar === '-') {
      let firstCharacter = value.substring(0,1);
      let remainValue = value.substring(1);
      firstCharacter  = firstCharacter.replace(/[^-\d]/g, '');
      remainValue = remainValue.replace(/\D/g, '');
      return firstCharacter.concat(remainValue);
    }
    return value.replace(/\D/g, '');
  }

}
